import constants from '../constants/constants';
import i18n from '../../plugins/i18n';
import { formatPrice, formatPriceAnalysis } from '../filters';
import $ from 'jquery';

/** Display fixed overall favorability baseline with 3 decimal places (half-width). */
export function formatOverallStdFixedDisplay(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return (Math.round(n * 1000) / 1000).toFixed(3);
}

/** Input text: optional leading minus, digits + one dot, at most 3 fractional digits (My page overall_std_fixed). */
export function sanitizeOverallStdFixedInputText(raw) {
  // Japanese fullwidth decimal point (． U+FF0E) -> ASCII period
  let v = String(raw ?? '').replace(/\uFF0E/g, '.');
  v = v.replace(/[^\d.\-]/g, '');
  const neg = v.startsWith('-');
  if (neg) {
    v = v.slice(1);
  }
  v = v.replace(/-/g, '');
  const parts = v.split('.');
  if (parts.length === 1) {
    const body = parts[0];
    if (neg && body === '') return '-';
    return neg ? `-${body}` : body;
  }
  const intPart = parts[0];
  const frac = parts.slice(1).join('').slice(0, 3);
  const body = frac.length ? `${intPart}.${frac}` : `${intPart}.`;
  return neg ? `-${body}` : body;
}

export function isJaLanguageAndMarket({ region, language, countryUser }) {
  return region === constants.REGION_JA
    && language === constants.LANGUAGE_JA
    && countryUser === constants.COUNTRY_USER_JP;
}

export function getUserDefaultGenerationCompanyName(authUser, localeContext = null) {
  if (localeContext && !isJaLanguageAndMarket(localeContext)) {
    return '';
  }
  if (!authUser || !authUser.has_default_generation_company_name || !authUser.default_generation_company_name) {
    return '';
  }
  return String(authUser.default_generation_company_name).trim();
}

export function getUserDefaultEvaluationCategoryId(authUser, categories = null) {
  if (!authUser || !authUser.has_default_evaluation_category || authUser.default_evaluation_category == null) {
    return null;
  }
  const id = parseInt(authUser.default_evaluation_category, 10);
  if (Number.isNaN(id)) {
    return null;
  }
  if (categories && !categories.some(category => category.id === id)) {
    return null;
  }
  return id;
}
export function parseJwt(token = '') {
  if (token) {
    const base64Url = token.split('.')[1];

    if (base64Url) {
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    }
  }

  return '';
}

export function convertToFullWidth(str) {
  var kanaMap = {
    'ｶﾞ': 'ガ', 'ｷﾞ': 'ギ', 'ｸﾞ': 'グ', 'ｹﾞ': 'ゲ', 'ｺﾞ': 'ゴ',
    'ｻﾞ': 'ザ', 'ｼﾞ': 'ジ', 'ｽﾞ': 'ズ', 'ｾﾞ': 'ゼ', 'ｿﾞ': 'ゾ',
    'ﾀﾞ': 'ダ', 'ﾁﾞ': 'ヂ', 'ﾂﾞ': 'ヅ', 'ﾃﾞ': 'デ', 'ﾄﾞ': 'ド',
    'ﾊﾞ': 'バ', 'ﾋﾞ': 'ビ', 'ﾌﾞ': 'ブ', 'ﾍﾞ': 'ベ', 'ﾎﾞ': 'ボ',
    'ﾊﾟ': 'パ', 'ﾋﾟ': 'ピ', 'ﾌﾟ': 'プ', 'ﾍﾟ': 'ペ', 'ﾎﾟ': 'ポ',
    'ｳﾞ': 'ヴ', 'ﾜﾞ': 'ヷ', 'ｦﾞ': 'ヺ',
    'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
    'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
    'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
    'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
    'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
    'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
    'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
    'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ',
    'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ',
    'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
    'ｧ': 'ァ', 'ｨ': 'ィ', 'ｩ': 'ゥ', 'ｪ': 'ェ', 'ｫ': 'ォ',
    'ｯ': 'ッ', 'ｬ': 'ャ', 'ｭ': 'ュ', 'ｮ': 'ョ',
    '｡': '。', '､': '、', 'ｰ': 'ー', '｢': '「', '｣': '」', '･': '・'
  };

  var reg = new RegExp('(' + Object.keys(kanaMap).join('|') + ')', 'g');
  return str
    .replace(reg, function (match) {
      return kanaMap[match];
    })
    .replace(/ﾞ/g, '゛')
    .replace(/ﾟ/g, '゜');
}

export function updatePlans(plans, date) {
  // only phase 5
  let checkInAround = false;
  if (date) {
    const dateStart = '2021-05-01 00:00:00';
    const dateEnd = '2021-05-31 00:00:00';
    if (date >= dateStart && date < dateEnd) {
      checkInAround = true;
    }
  }
  plans[0].time_expried_phase5 = checkInAround;

  return plans;
}

export function checkIsShowExactly(date) {
  // check hidden 3.選択カテゴリーの精度 in Result Screen
  const dateCheck = '2021-06-09 00:00:00';
  return date < dateCheck;
}

export function saveRouteAfter(from, to) {
  if (to.name === 'AutoCreateResult' && to.params.project_id) {
    localStorage.setItem('result', to.params.project_id);
  } else if (to.name === 'Login' || to.name === 'MypageIndex' || to.name === 'Users') {
    // TODO
  } else {
    localStorage.removeItem('result');
  }
}

export function setNameFileZip(data) {
  let nameProject = data.name.slice(0, 10) + '_';
  const segments = constants.SEGMENT_LIST;
  segments.forEach((group) => {
    group.forEach((segment) => {
      if (segment.id === data.segments[0].type) {
        nameProject += segment.text + '.zip';
      }
    });
  });
  return nameProject;
}

export function checkSafari() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf('safari') != -1) {
    if (ua.indexOf('chrome') > -1) {
      return false;
    } else {
      return true; // Safari
    }
  }
  return false;
}

/**
 * Defer work until the browser is idle. Safari lacks requestIdleCallback.
 * @param {Function} callback
 * @param {{ timeout?: number }} [options]
 */
export function scheduleIdleCallback(callback, options = {}) {
  const { timeout = 5000 } = options;
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 0);
  }
}

export function sortByLayer(image) {
  const arrayNameLayers = image.layers.map(layer => layer.name).sort();
  const layers = [];
  arrayNameLayers.forEach((name) => {
    image.layers.forEach((layer) => {
      if (name === layer.name) {
        layers.push(layer);
      }
    });
  });
  return { ...image, layers };
}

export function addMonths(date, months) {
  const d = date.getDate();
  date.setMonth(date.getMonth() + months);
  if (date.getDate() !== d) {
    date.setDate(0);
  }
  return date;
}

export function getLanguageTitleByKey(key) {
  const language = constants.LANGUAGE;
  for (let i = 0; i < language.length; i++) {
    if (language[i].key === key) {
      return language[i].title;
    }
  }
  return null;
}

export function getArrayValueCategory(targetCountry, region) {
  switch (targetCountry) {
    case constants.TARGET_COUNTRY_CN:
      return constants.CATEGORIES_CN;
    case constants.TARGET_COUNTRY_TH:
      return constants.CATEGORIES_TH;
    case constants.TARGET_COUNTRY_ID:
      return constants.CATEGORIES_ID;
    case constants.TARGET_COUNTRY_JP && region === constants.REGION_CN:
      return constants.CATEGORIES_JP_FOR_REGION_CN;
    default:
      return constants.CATEGORIES_JP;
  }
}

export function getCategoriesSingle(region) {
  if (region === constants.REGION_CN) {
    return constants.CATEGORIES_SINGLE_FOR_REGION_CN;
  }
  return constants.CATEGORIES_SINGLE;
}

export function pushDataToCategorySelect(category, categoriesSelect, label) {
  categoriesSelect.forEach((item, index) => {
    if (item.label === label) {
      categoriesSelect[index].options.push(category);
    }
  });
  return categoriesSelect;
}

export function formatCategories(categories, targetCountry, region, t) {
  const sortedCategories = categories.slice().sort((a, b) => a.order - b.order);
  let categoriesSelect = [];
  const categoryGroups = [
    {
      categoryValues: constants.CATEGORIES_ALCOHOL,
      label: t('survey_project.select_category_option.alcohol'),
    },
    {
      categoryValues: constants.CATEGORIES_BEVERAGE,
      label: t('survey_project.select_category_option.beverage'),
    },
    {
      categoryValues: constants.CATEGORIES_DESSERTS,
      label: t('survey_project.select_category_option.desserts'),
    },
    {
      categoryValues: constants.CATEGORIES_ROOM_TEMPERATURE_FOOD,
      label: t('survey_project.select_category_option.room_temperature_food'),
    },
    {
      categoryValues: constants.CATEGORIES_CONFECTIONERY,
      label: t('survey_project.select_category_option.confectionery'),
    },
    {
      categoryValues: constants.CATEGORIES_COSMETICS,
      label: t('survey_project.select_category_option.cosmetics'),
    },
    {
      categoryValues: constants.CATEGORIES_PHARMACEUTICALS,
      label: t('survey_project.select_category_option.pharmaceuticals'),
    },
    {
      categoryValues: constants.CATEGORIES_HYGIENE_PRODUCTS,
      label: t('survey_project.select_category_option.hygiene_products'),
    },
    {
      categoryValues: constants.CATEGORIES_PET,
      label: t('survey_project.select_category_option.pet'),
    },
  ];
  const categoriesSingle = getCategoriesSingle(region);

  sortedCategories.forEach((category) => {
    const categoryValue = getValueCategory(category, targetCountry);

    categoryGroups.forEach((group) => {
      if (group.categoryValues.includes(categoryValue)) {
        if (!categoriesSelect.some(item => item.label === group.label)) {
          categoriesSelect.push({ label: group.label, options: [] });
        }
        categoriesSelect = pushDataToCategorySelect(category, categoriesSelect, group.label);
      }
    });

    if (categoriesSingle.includes(categoryValue)) {
      categoriesSelect.push({
        label: category.name,
        options: [category],
        isOnly: true,
      });
    }
  });

  return categoriesSelect;
}

export function getSelect2DropdownParent($select) {
  if (!$select || !$select.length) {
    return $(document.body);
  }
  const $modal = $select.first().closest('.modal');
  return $modal.length ? $modal : $(document.body);
}

export function getCountWord(targetCountry) {
  return parseInt(targetCountry, 10) === constants.TARGET_COUNTRY_CN ? constants.MAX_SELECTED_WORD_CN : constants.MAX_SELECTED_WORD;
}

export function getListWordByTargetCountry(words, targetCountry) {
  if (parseInt(targetCountry, 10) !== constants.TARGET_COUNTRY_CN) {
    return words.filter(item => item.id !== 20);
  }
  return words;
}

export function getTextTargetCountry(targetCountry) {
  const result = constants.LIST_TARGET_COUNTRY.find(item => item.id === targetCountry);
  if (result) {
    const locale = localStorage.getItem('language') ? localStorage.getItem('language') : 'ja';
    return result.text[locale];
  }
  return '';
}

export function sortImage(images) {
  if (images.length > 0) {
    return images
      .slice()
      .sort((a, b) => (a.final_score < b.final_score
        ? 1
        : b.final_score < a.final_score
          ? -1
          : 0));
  }
  return images;
}

export function getSrcImage(order, imagesBase64) {
  const found = imagesBase64.find(element => element.order === order);
  if (found) {
    return found.src;
  }
  return '';
}
export function getImagesChart(project, segmentFilterSelected, sortScore = false, key) {
  const imagesChart = [];
  segmentFilterSelected.forEach((segmentId, index) => {
    let imageChart = [];
    const keySegment = project[key].findIndex(segment => segmentId === segment.type);
    if (index === 0 || sortScore) {
      imageChart = sortImage(project[key][keySegment].images);
    } else {
      imagesChart[0].forEach((image) => {
        project[key][keySegment].images.forEach((item) => {
          if (image.order === item.order) {
            imageChart.push(item);
          }
        });
      });
    }
    imagesChart.push(imageChart);
  });
  return imagesChart;
}

export function isNumeric(value) {
  return /^-?\d+$/.test(value);
}

/**
 * Normalize a project name for safe use in download file names.
 * @param {*} text
 * @returns {string}
 */
export function normalizeFullwidthToHalfwidth(text) {
  // Step 1: guard
  if (text == null) return '';
  const str = String(text);

  // Step 2: remove full-width ideographic space
  const noFwSpace = str.replace(/\u3000/g, '');

  // Step 3: remove full-width special/currency chars BEFORE mapping.
  const noFwSpecial = noFwSpace.replace(/[\uFF01-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF60\uFFE0-\uFFE6]/g, '');

  // Step 4: map remaining full-width ASCII variants (FF01–FF5E) → ASCII (21–7E).
  // Using explicit char-by-char shift instead of NFKC to avoid converting half-width katakana (FF65–FF9F) into full-width katakana.
  const FW_ASCII_OFFSET = 0xFEE0;
  return [...noFwSpecial].map(ch => {
    const cp = ch.codePointAt(0);
    return (cp >= 0xFF01 && cp <= 0xFF5E) ? String.fromCodePoint(cp - FW_ASCII_OFFSET) : ch;
  }).join('');
}

/**
 * Build a safe download base name for Mac/Safari and other OSes.
 * @param {*} text
 * @param {number} maxLen
 * @returns {string}
 */
export function sanitizeDownloadFileName(text, maxLen = 100) {
  const normalized = normalizeFullwidthToHalfwidth(text)
    // Zero-width / BOM chars often pasted from docs — break download filenames
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    // Path-unsafe (Mac/Windows) + common CSV/filename noise
    .replace(/[\\/:*?"<>|&#!@+;$~%'{},.\u0000-\u001F\u007F\uFF08\uFF09]/g, '')
    .trim()
    .substring(0, maxLen)
    .trim()
    .replace(/_+$/, '');
  return normalized || 'download';
}

export function getFileNameGenerate(projectName, prompt, last = '') {
  // When changing the filename max length, also update ZIP_IMAGE_FILE_NAME_MAX_LEN on the backend.

  // First normalize full-width
  projectName = normalizeFullwidthToHalfwidth(projectName);
  if (prompt) {
    prompt = normalizeFullwidthToHalfwidth(prompt);
  }

  // Keep `_` and inner whitespace; only trim ends. Sync charset with BE _ZIP_FILE_NAME_RE.
  const regex = /[\\\/:*?"<>|&#!@+;$~%'{},.\u0000-\u001F\u007F\uFF08\uFF09]/g;
  let fileName = projectName.replace(regex, '').trim().slice(0, 30).trim();
  if (prompt) {
    const promptText = prompt.replace(regex, '').trim().slice(0, 30).trim();
    fileName = `${fileName}_${promptText}`;
  }
  return `${fileName}_${last}`;
}

export function convertCountryToTargetCountry(country) {
  const mapping = {
    jp: constants.TARGET_COUNTRY_JP,
    cn: constants.TARGET_COUNTRY_CN,
    th: constants.TARGET_COUNTRY_TH,
    id: constants.TARGET_COUNTRY_ID,
    ot: constants.TARGET_COUNTRY_JP,
  };
  for (const [key, value] of Object.entries(mapping)) {
    if (country === key) {
      return value;
    }
  }
  return constants.TARGET_COUNTRY_NONE;
}

export function formatNameOldProject(name) {
  if (name.length > 50) {
    return `${name.substring(0, 50)}...`;
  }
  return name;
}

export function getTextPriceMenu(numImage, countAnalysis, plan, language, country, showCostAnalysis, targetCountry = null) {
  let res = '';
  if (countAnalysis > 0 && showCostAnalysis) {
    res += i18n.t('survey_project.select_menu_cost_survey', { numImage });
    res += ` ${formatPrice(plan, language, country, numImage, targetCountry)}`;
    res += ` ${i18n.t('survey_project.select_menu_cost_analysis', { numImage: countAnalysis })}`;
    res += ` ${formatPriceAnalysis(countAnalysis)}`;
  } else {
    res += i18n.t('survey_project.select_menu_cost', { numImage });
    res += ` ${formatPrice(plan, language, country, numImage, targetCountry)}`;
  }
  return res;
}

export function addServiceFacebook(to) {
  const region = process.env.VUE_APP_REGION || 'ja';
  if (region === constants.REGION_JA) {
    const routersExclude = [
      'PreRegister',
      'MailSend',
      'RegisterIndex',
      'PlanSelect',
      'RegisterConfirm',
      'RegisterComplete',
    ];
    const fbNoscript = document.getElementById('fb-noscript');
    const fbPixel = document.getElementById('fb-pixel');
    if (!fbPixel && !routersExclude.includes(to.name)) {
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = 'https://www.facebook.com/tr?id=964564024221596&ev=PageView&noscript=1';
      img.id = 'fb-pixel';

      fbNoscript.appendChild(img);
    } else if (fbPixel && routersExclude.includes(to.name)) {
      fbPixel.remove();
    }
  }
}

export function getValueCategory(category, targetCountry) {
  if (targetCountry === constants.TARGET_COUNTRY_CN) {
    return category.value_cn;
  }
  if (targetCountry === constants.TARGET_COUNTRY_TH) {
    return category.value_th;
  }
  if (targetCountry === constants.TARGET_COUNTRY_ID) {
    return category.value_id;
  }
  return category.value;
}

export function hasSubscribedPlan(user) {
  return (user.plan || user.plan_id);
}

export function hasSubscribedPlanGenerate(user) {
  return (user.country === constants.COUNTRY_USER_JP && (user.plan_generate || user.generate_begin_at));
}

export function hasSubscribedPlanKeyVisual(user) {
  return (user.plan_key_visual || user.key_visual_begin_at);
}

export function hasSubscribedPlanOption(user) {
  return (user.plan_option || user.option_begin_at);
}

export function isTimeExpired(timeExpiration) {
  if (timeExpiration === 0) {
    return false;
  }
  const timeExpired = new Date(timeExpiration).getTime() * 1000;
  const now = new Date().getTime();
  return timeExpired < now;
}


export function shouldShowGenerationHistory(authUser, isUserPlanGenerate) {
  // AWS Japan (REGION_JA) only
  const region = process.env.VUE_APP_REGION || 'ja';
  if (region !== constants.REGION_JA) return false;
  if (!authUser || !authUser.id) return false;
  if (authUser.country !== constants.COUNTRY_USER_JP) return false;

  if (isUserPlanGenerate) return true;

  const lastGenerateAt = Number(authUser.last_generate_at);
  if (!lastGenerateAt) return false;

  const retentionDays = Number(authUser.retention_time_generate);
  const days = Number.isFinite(retentionDays) && retentionDays > 0
    ? retentionDays
    : constants.GENERATION_HISTORY_RETENTION_DAYS_DEFAULT;

  const ageSeconds = (Date.now() / 1000) - lastGenerateAt;
  return ageSeconds <= days * 24 * 60 * 60;
}

export function isGenerateFreePlanActive(authUser, isUserPlanGenerate) {
  if (!authUser || !authUser.plan_generate) return false;
  if (!isUserPlanGenerate) return false;
  const planGenerateId = authUser.plan_generate.plan_generate_id ?? authUser.plan_generate.id;
  if (planGenerateId !== constants.PLAN_GENERATE_FREE_ID) return false;
  // Treat generate_expired_at = 0/null as "no expiration" (active)
  return !authUser.generate_expired_at || !isTimeExpired(authUser.generate_expired_at);
}

export function isGenerateAdhocPlanActive(authUser, isUserPlanGenerate) {
  if (!authUser || !authUser.plan_generate) return false;
  if (!isUserPlanGenerate) return false;
  const planGenerateId = authUser.plan_generate.plan_generate_id ?? authUser.plan_generate.id;
  return planGenerateId === constants.PLAN_GENERATE_ADHOC_ID;
}

export function shouldConfirmLogoutDuringGenerateConceptProject(
  authUser,
  isUserPlanGenerate,
  isOnGenerateConceptResultScreen,
) {
  return isGenerateFreePlanActive(authUser, isUserPlanGenerate)
    && isOnGenerateConceptResultScreen;
}

export function shouldShowChangeToUnlimitedPlan(authUser, isUserPlanGenerate) {
  if (!authUser) return false;
  const generatePlan = authUser.plan_generate;
  if (!generatePlan) return false;
  if (!hasSubscribedPlanGenerate(authUser) || !isUserPlanGenerate) return false;

  // Must NOT have an active valuation plan.
  const hasActiveValuationPlan = !!(authUser.plan && (
    authUser.plan.plan_type === constants.PLAN_TYPE_ADHOC
      ? true
      : !isTimeExpired(authUser.expired_at)
  ));
  if (hasActiveValuationPlan) return false;

  // Generate plan must be the FREE generate plan (support both shapes).
  const generatePlanId = generatePlan.plan_generate_id ?? generatePlan.id;
  return generatePlanId === constants.PLAN_GENERATE_FREE_ID;
}

export function checkUserTrial(authUser) {
  return authUser && authUser.plan && authUser.plan.plan_type === constants.PLAN_TYPE_TRIAL;
}

export function normalizeEmptyDisplayValue(value) {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

export function isEmptyProfile(user) {
  if (!user) {
    return true;
  }
  return normalizeEmptyDisplayValue(user.organization_name) === ''
    && normalizeEmptyDisplayValue(user.branch_office) === '';
}

export function formatOrganizationOfficeName(user) {
  if (isEmptyProfile(user)) {
    return constants.DATA_HIDDEN;
  }

  const text = [user.organization_name, user.branch_office]
    .map(normalizeEmptyDisplayValue)
    .filter(Boolean)
    .join(' ');

  return text || constants.DATA_HIDDEN;
}

export function formatProfileFieldWhenTrial(user, value) {
  if (isEmptyProfile(user)) {
    return constants.DATA_HIDDEN;
  }

  const text = normalizeEmptyDisplayValue(value);
  return text || constants.DATA_HIDDEN;
}

export function formatJoinedProfileField(user, ...values) {
  if (isEmptyProfile(user)) {
    return constants.DATA_HIDDEN;
  }

  const text = values
    .map(normalizeEmptyDisplayValue)
    .filter(Boolean)
    .join(' ');

  return text || constants.DATA_HIDDEN;
}

export function formatCountryWhenTrial(user, countryMap, language) {
  if (isEmptyProfile(user)) {
    return constants.DATA_HIDDEN;
  }

  const country = user.country;
  if (country == null || !countryMap[country]) {
    return constants.DATA_HIDDEN;
  }

  return normalizeEmptyDisplayValue(countryMap[country][language]) || constants.DATA_HIDDEN;
}

export function formatOwnerDisplayName(user) {
  if (!user) {
    return constants.DATA_HIDDEN;
  }

  const lastName = user.group ? user.group.last_name : user.last_name;
  const firstName = user.group ? user.group.first_name : user.first_name;

  return formatJoinedProfileField(user, lastName, firstName);
}

export function isUserWithoutProfileInfo(authUser) {
  if (!authUser) return false;
  return !authUser.organization_name && !authUser.branch_office;
}

export function isGenerateFreeTrialWithoutUserInfo(authUser, isUserPlanGenerate) {
  if (!authUser || !authUser.plan_generate) return false;
  if (!isUserPlanGenerate) return false;

  const generatePlanId = authUser.plan_generate.plan_generate_id ?? authUser.plan_generate.id;
  if (generatePlanId !== constants.PLAN_GENERATE_FREE_ID) return false;

  return isUserWithoutProfileInfo(authUser);
}

export function getUserDisplayNameProjectList(projectUser, currentUser) {
  if (Number(projectUser.id) === Number(currentUser.id)) {
    return '';
  }
  const name = `${projectUser.last_name || ''} ${projectUser.first_name || ''}`.trim();
  return `(${i18n.t('project_list_created_by', { name: name })})`;
}

export function removeRecaptcha() {
  const recaptchaEl = document.querySelector('.g-recaptcha');
  if (recaptchaEl) {
    recaptchaEl.innerHTML = '';
    recaptchaEl.remove();
  }

  document.querySelectorAll('iframe[src*="recaptcha"]').forEach((iframe) => {
    iframe.remove();
  });

  document.querySelectorAll('iframe[title*="reCAPTCHA"]').forEach((iframe) => {
    iframe.remove();
  });

  document.querySelectorAll('script[src*="recaptcha"]').forEach((script) => {
    script.remove();
  });

  document.querySelectorAll('link[href*="recaptcha"]').forEach((style) => {
    style.remove();
  });

  document.querySelectorAll('.g-recaptcha-bubble-arrow').forEach((bubbleArrowEl) => {
    if (bubbleArrowEl.parentNode) {
      bubbleArrowEl.parentNode.remove();
    }
  });

  if (window.grecaptcha) {
    delete window.grecaptcha;
  }

  if (window.recaptchaCallback) {
    delete window.recaptchaCallback;
  }
}

export function getNameSegment(type) {
  switch (type) {
    case constants.MENU_FOR_OVER_ALL:
      return 'segments_over_all';
    case constants.MENU_FOR_HEAT_MAP:
      return 'segments_heat_map';
    case constants.MENU_FOR_IMAGEWORD:
      return 'segments_image_word';
    case constants.MENU_FOR_VARIANCE:
      return 'segments_variance';
    case constants.MENU_FOR_CONCEPT_MATCHING:
      return 'segments_concept_matching';
    default:
      return '';
  }
}

export function buildProjectSegmentsSnapshot(project) {
  const p = project;
  if (!p || !p.id) return null;
  const snapshot = {
    id: p.id,
    project_type: Array.isArray(p.project_type) ? p.project_type : [],
  };
  // Keep only segment blobs needed by SelectSegment (eligible union in Report All).
  constants.menu.forEach((menuItem) => {
    const key = getNameSegment(menuItem.id);
    if (!key) return;
    if (p[key] != null) {
      snapshot[key] = p[key];
    }
  });
  return snapshot;
}

export async function getWidthHeightImage(base64) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = reject;
    img.src = base64;
  });
}

export function getElementSizeById(id) {
  const el = document.getElementById(id);
  if (!el) return null;

  const rect = el.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
  };
}

export function checkIsProduction() {
  // The project has functionalities that only need to be deployed to dev 1.
  // check is prod, dev 2 same prod
  const nodeEnv = process.env.NODE_ENV;
  return [constants.DEV_ENV_2, constants.PROD].includes(nodeEnv);
}

/** Convert API remaining_seconds to whole minutes for UI (min 1). */
export function remainingMinutesFromSeconds(seconds) {
  if (seconds == null || seconds === '') return 1;
  const n = Number(seconds);
  if (Number.isNaN(n) || n < 0) return 1;
  return Math.max(1, Math.ceil(n / 60));
}

/** Survey flow: parse a project id-like value into a positive number, else null. */
export function parsePositiveProjectId(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}
